/**
 * Dialog for admins to search and impersonate users
 * Excludes admin users from search results
 */

import { useFetcher } from '@remix-run/react'
import { Search, UserCog, Loader2 } from 'lucide-react'
import { useEffect, useState, useCallback } from 'react'
import { getUserImgSrc } from '#app/utils/misc'
import { Button } from './ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from './ui/dialog'
import { Input } from './ui/input'

type SearchUser = {
	id: string
	username: string
	name: string | null
	image: { id: string } | null
	roles: { name: string }[]
}

type SearchResponse = {
	users?: SearchUser[]
	error?: string
	message?: string
}

export function UserImpersonationDialog() {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const searchFetcher = useFetcher<SearchResponse>()
	const impersonateFetcher = useFetcher()

	const isSearching = searchFetcher.state === 'loading'
	const isImpersonating = impersonateFetcher.state !== 'idle'

	// Debounced search
	const search = useCallback(
		(q: string) => {
			if (q.length >= 2) {
				searchFetcher.load(`/api/users-search?q=${encodeURIComponent(q)}`)
			}
		},
		[searchFetcher],
	)

	useEffect(() => {
		const timer = setTimeout(() => {
			search(query)
		}, 300)
		return () => clearTimeout(timer)
	}, [query, search])

	// Close dialog after successful impersonation
	useEffect(() => {
		if (impersonateFetcher.state === 'idle' && impersonateFetcher.data) {
			const data = impersonateFetcher.data as { success?: boolean }
			if (data.success) {
				setOpen(false)
				setQuery('')
				// Force a full page reload to get the new user context
				window.location.reload()
			}
		}
	}, [impersonateFetcher.state, impersonateFetcher.data])

	const users = searchFetcher.data?.users || []
	const searchError = searchFetcher.data?.error

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="outline" size="sm" className="gap-1">
					<UserCog className="h-4 w-4" />
					<span className="hidden sm:inline">Impersonate</span>
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Impersonate User</DialogTitle>
					<DialogDescription>
						Search for a user to view the app from their perspective. Admin
						users cannot be impersonated.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Search by username or name..."
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							className="pl-9"
							autoFocus
						/>
						{isSearching && (
							<Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
						)}
					</div>

					{searchError && (
						<p className="text-sm text-destructive">{searchError}</p>
					)}

					{query.length > 0 && query.length < 2 && (
						<p className="text-sm text-muted-foreground">
							Type at least 2 characters to search
						</p>
					)}

					{users.length > 0 && (
						<div className="max-h-60 space-y-1 overflow-y-auto">
							{users.map((user) => (
								<impersonateFetcher.Form
									key={user.id}
									method="post"
									action="/api/impersonate"
								>
									<input type="hidden" name="intent" value="start" />
									<input type="hidden" name="userId" value={user.id} />
									<button
										type="submit"
										disabled={isImpersonating}
										className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-accent disabled:opacity-50"
									>
										<img
											src={getUserImgSrc(user.image?.id)}
											alt=""
											className="h-8 w-8 rounded-full object-cover"
										/>
										<div className="flex-1 overflow-hidden">
											<p className="truncate font-medium">
												{user.name || user.username}
											</p>
											<p className="truncate text-sm text-muted-foreground">
												@{user.username}
												{user.roles.length > 0 && (
													<span className="ml-2">
														({user.roles.map((r) => r.name).join(', ')})
													</span>
												)}
											</p>
										</div>
									</button>
								</impersonateFetcher.Form>
							))}
						</div>
					)}

					{query.length >= 2 && !isSearching && users.length === 0 && (
						<p className="text-sm text-muted-foreground">
							No users found matching "{query}"
						</p>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
